import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { Drawer, Select, TextField } from "@outpave/ds";
import { toast } from "react-toastify";
import ApiKeyRole, {
  API_KEY_ROLE_OPTIONS,
  API_KEY_DEFAULT_LIMITS,
} from "enums/ApiKeyRole";
import ApiKeyScope from "enums/ApiKeyScope";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { CreateApiKeyInput, ApiKey } from "hooks/useApiKeys";
import { toastMutationError } from "utils/toastMutationError";
import { Inline } from "components/layout/reusableLayouts";
import { apiKeyRoleLabelKey } from "utils/apiKeys/apiKeyStatus";
import IpAllowlistField, { AllowListMode } from "./IpAllowlistField";

interface ApiKeyCreateDrawerProps {
  open: boolean;
  scope: ApiKeyScope;
  ownerId: string;
  onClose: () => void;
  onCreate: (
    input: CreateApiKeyInput,
  ) => Promise<{ apiKey: ApiKey; secret: string }>;
  // Called with the freshly-minted key + plaintext secret so the parent can
  // surface the reveal dialog. The drawer hands off and closes itself.
  onSecretReveal: (result: { apiKey: ApiKey; secret: string }) => void;
}

/** Each of the two rate limit fields takes half the row and may shrink. */
const half = { flex: 1, minWidth: 0 } as const;

/**
 * A rate limit is a whole number of requests. GraphQL Int takes a negative one
 * happily and it becomes the limiter's max, so the form refuses it here.
 */
const isWholeCount = (value: string): boolean => {
  if (!value) return true;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0;
};

const ApiKeyCreateDrawer: React.FC<ApiKeyCreateDrawerProps> = ({
  open,
  scope,
  ownerId,
  onClose,
  onCreate,
  onSecretReveal,
}) => {
  const { t } = useTranslation();
  // Customers cannot raise their own ceilings. PlatformOwner, not
  // PlatformAdmin: assertCanSetRateLimits in the ApiKey resolver requires
  // PlatformOwner, so a PlatformAdmin shown these fields could only ever get
  // Unauthorized Access back from any override they typed.
  const canEditRateLimits = useCheckUserRole(Role.PlatformOwner);
  const [name, setName] = useState("");
  const [role, setRole] = useState<ApiKeyRole>(ApiKeyRole.ReadOnly);
  const [expiresAt, setExpiresAt] = useState<string>(""); // YYYY-MM-DD or ""
  const [allowListMode, setAllowListMode] = useState<AllowListMode>("ALL");
  const [allowListIps, setAllowListIps] = useState<string[]>([]);
  const [allowListIpDraft, setAllowListIpDraft] = useState("");
  const [rateLimitSustained, setRateLimitSustained] = useState<string>(
    String(API_KEY_DEFAULT_LIMITS[ApiKeyRole.ReadOnly].sustainedPerHour),
  );
  const [rateLimitBurst, setRateLimitBurst] = useState<string>(
    String(API_KEY_DEFAULT_LIMITS[ApiKeyRole.ReadOnly].burst),
  );
  const [submitting, setSubmitting] = useState(false);

  const roleDefaults = API_KEY_DEFAULT_LIMITS[role];

  // Re-sync the prefill when role changes. Overrides typed under the previous
  // role get reset: changing role is a deliberate "reconsider limits" action.
  useEffect(() => {
    setRateLimitSustained(String(roleDefaults.sustainedPerHour));
    setRateLimitBurst(String(roleDefaults.burst));
  }, [role, roleDefaults.sustainedPerHour, roleDefaults.burst]);

  const reset = () => {
    setName("");
    setRole(ApiKeyRole.ReadOnly);
    setExpiresAt("");
    setAllowListMode("ALL");
    setAllowListIps([]);
    setAllowListIpDraft("");
    setRateLimitSustained(
      String(API_KEY_DEFAULT_LIMITS[ApiKeyRole.ReadOnly].sustainedPerHour),
    );
    setRateLimitBurst(
      String(API_KEY_DEFAULT_LIMITS[ApiKeyRole.ReadOnly].burst),
    );
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error(t("ApiKeyNameRequired"));
      return;
    }
    if (allowListMode === "RESTRICT" && allowListIpDraft.trim()) {
      toast.error(t("ApiKeyConfirmIpEntry"));
      return;
    }
    if (allowListMode === "RESTRICT" && allowListIps.length === 0) {
      toast.error(t("ApiKeyAddAtLeastOneIp"));
      return;
    }
    if (
      canEditRateLimits &&
      (!isWholeCount(rateLimitSustained) || !isWholeCount(rateLimitBurst))
    ) {
      toast.error(t("ApiKeyRateLimitWholeNumber"));
      return;
    }
    setSubmitting(true);
    try {
      const input: CreateApiKeyInput = {
        scope,
        ownerId,
        name: name.trim(),
        role,
        expiresAt: expiresAt ? new Date(expiresAt).getTime() : null,
        allowListIp: allowListMode === "ALL" ? "ALL" : allowListIps.join(","),
        // Send null when the value matches the role default so the key tracks
        // future default changes; only send a number when the user overrode it.
        rateLimitSustained:
          rateLimitSustained &&
          Number(rateLimitSustained) !== roleDefaults.sustainedPerHour
            ? Number(rateLimitSustained)
            : null,
        rateLimitBurst:
          rateLimitBurst && Number(rateLimitBurst) !== roleDefaults.burst
            ? Number(rateLimitBurst)
            : null,
      };
      const result = await onCreate(input);
      onSecretReveal(result);
      reset();
      onClose();
    } catch (err: unknown) {
      toastMutationError(err, t("ApiKeyCreateError"));
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Create API key")}
      loading={submitting}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={handleSubmit}
            disabled={submitting || !name.trim()}
          >
            {t("Create key")}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("ApiKeyCreateDescription")}
      </Typography>

      <TextField
        label={t("Name")}
        placeholder={t("ApiKeyNamePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        disabled={submitting}
      />

      <Select
        label={t("Role")}
        value={role}
        onChange={(value) => setRole((value as ApiKeyRole) ?? role)}
        clearable={false}
        fullWidth
        disabled={submitting}
        helperText={t("ApiKeyRoleHelper")}
        options={API_KEY_ROLE_OPTIONS.map((r) => ({
          id: r,
          label: t(apiKeyRoleLabelKey(r)),
        }))}
      />

      <TextField
        label={t("Expires at (optional)")}
        type="date"
        value={expiresAt}
        onChange={(e) => setExpiresAt(e.target.value)}
        fullWidth
        disabled={submitting}
      />

      <IpAllowlistField
        mode={allowListMode}
        onModeChange={setAllowListMode}
        entries={allowListIps}
        onEntriesChange={setAllowListIps}
        draft={allowListIpDraft}
        onDraftChange={setAllowListIpDraft}
        disabled={submitting}
      />

      {canEditRateLimits && (
        <Inline gap="var(--space-5)" align="start" wrap={false}>
          <Box sx={half}>
            <TextField
              label={t("Sustained rate (per hour)")}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={rateLimitSustained}
              onChange={(e) => setRateLimitSustained(e.target.value)}
              fullWidth
              disabled={submitting}
            />
          </Box>
          <Box sx={half}>
            <TextField
              label={t("Burst rate (max in 10 seconds)")}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={rateLimitBurst}
              onChange={(e) => setRateLimitBurst(e.target.value)}
              fullWidth
              disabled={submitting}
            />
          </Box>
        </Inline>
      )}
    </Drawer>
  );
};

export default ApiKeyCreateDrawer;
